import { ChatBotUIDateSlot } from "@jugl-web/domain-resources/chat-bot/types";
import { PlainButton } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { useDatePicker } from "@rehookify/datepicker";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import sub from "date-fns/sub";
import add from "date-fns/add";
import { FC, useMemo } from "react";
import { TimezoneSelect } from "./TimezoneSelect";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

interface DaySlotPickerProps {
  slots: ChatBotUIDateSlot[];
  clientTimezoneCode: string;
  businessTimezoneCode: string;
  selectedTimezoneType: "business" | "client";
  onSelect: (day: Date) => void;
  onTimezoneTypeChange: (timezoneType: "business" | "client") => void;
}

export const DaySlotPicker: FC<DaySlotPickerProps> = ({
  slots,
  clientTimezoneCode,
  businessTimezoneCode,
  selectedTimezoneType,
  onSelect,
  onTimezoneTypeChange,
}) => {
  const availableDayTimestamps = useMemo<Set<number>>(() => {
    const set = new Set<number>();

    slots.forEach((slot) => {
      set.add(startOfDay(slot.startTime).getTime());
    });

    return set;
  }, [slots]);

  const minDate = useMemo<Date | undefined>(() => {
    if (availableDayTimestamps.size === 0) {
      return undefined;
    }

    const earliest = Math.min(...Array.from(availableDayTimestamps));

    return new Date(earliest);
  }, [availableDayTimestamps]);

  const maxDate = useMemo<Date | undefined>(() => {
    if (availableDayTimestamps.size === 0) {
      return undefined;
    }

    const latest = Math.max(...Array.from(availableDayTimestamps));

    return endOfDay(new Date(latest));
  }, [availableDayTimestamps]);

  const {
    data: { calendars, weekDays, months },
    propGetters: { subtractOffset, addOffset, dayButton, setOffset },
  } = useDatePicker({
    selectedDates: [],
    onDatesChange: () => {
      // No-op as the component is fully controlled by the `onSelect` callback
    },
    calendar: {
      // Start from Sunday to fit the American calendar layout
      startDay: 0,
    },
    dates: {
      minDate,
      maxDate,
      mode: "single",
    },
    locale: {
      day: "numeric",
      weekday: "short",
      monthName: "long",
      locale: "en",
    },
  });

  // Workaround prop getters to fix the issue with min/max date and moving between months
  const getPreviousMonthButtonProps = () => {
    const startOfCurrentMonth = months.find((m) => m.active)?.$date;

    if (!startOfCurrentMonth) {
      return subtractOffset({ months: 1 });
    }

    const endOfPreviousMonth = sub(startOfCurrentMonth, { seconds: 1 });

    return setOffset(endOfPreviousMonth);
  };

  const getNextMonthButtonProps = () => {
    const startOfCurrentMonth = months.find((m) => m.active)?.$date;

    if (!startOfCurrentMonth) {
      return addOffset({ months: 1 });
    }

    const startOfNextMonth = add(startOfCurrentMonth, { months: 1 });

    return setOffset(startOfNextMonth);
  };

  const calendar = calendars[0];

  if (!calendar) {
    return null;
  }

  const { month, year, days } = calendar;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex w-full items-center justify-between gap-2">
        <PlainButton
          {...getPreviousMonthButtonProps()}
          className={cx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            "cursor-pointer text-grey transition",
            "hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
          )}
        >
          <ArrowLeftIcon />
        </PlainButton>
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-secondary text-[15px] leading-[20px] font-medium text-dark">
            {month}, {year}
          </span>
          {clientTimezoneCode !== businessTimezoneCode && (
            <TimezoneSelect
              clientTimezoneCode={clientTimezoneCode}
              businessTimezoneCode={businessTimezoneCode}
              selectedTimezoneType={selectedTimezoneType}
              onSelect={onTimezoneTypeChange}
            />
          )}
        </div>
        <PlainButton
          {...getNextMonthButtonProps()}
          className={cx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            "cursor-pointer text-grey transition",
            "hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
          )}
        >
          <ArrowRightIcon />
        </PlainButton>
      </div>

      {/* Weekday headings */}
      <div className="mt-3 grid grid-cols-7">
        {weekDays.map((weekDay) => (
          <div
            key={`${month}-${year}-${weekDay}`}
            className="flex h-8 w-8 items-center justify-center font-secondary text-xs leading-[140%] text-grey"
          >
            {weekDay.slice(0, 2)}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="mt-[5px] grid grid-cols-7 gap-0.5">
        {days.map((dpDay) => {
          const isCurrentMonth = dpDay.inCurrentMonth;

          const isAvailable =
            isCurrentMonth &&
            availableDayTimestamps.has(startOfDay(dpDay.$date).getTime());

          const { onClick, ...buttonProps } = dayButton(dpDay, {
            disabled: !isAvailable,
          });

          return (
            <button
              key={dpDay.$date.toISOString()}
              type="button"
              className={cx(
                "flex h-8 w-8 items-center justify-center rounded-lg border-none bg-transparent font-secondary text-sm leading-[140%] transition",
                isCurrentMonth && isAvailable
                  ? "cursor-pointer text-dark hover:bg-grey-100 active:bg-grey-200"
                  : "cursor-not-allowed text-dark-300 line-through",
                !isCurrentMonth && "invisible"
              )}
              onClick={(event) => {
                onClick?.(event);

                if (isAvailable) {
                  onSelect(dpDay.$date);
                }
              }}
              {...buttonProps}
            >
              {dpDay.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};
